import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { RoleLabels } from "../utils/access";
import styles from "./InviteAccept.module.css";

const fmtDateTime = (d: any) => {
  try {
    return d ? new Date(d).toLocaleString() : "—";
  } catch {
    return "—";
  }
};

const roleLabel = (roleId: any) => RoleLabels[Number(roleId)] ?? "Unknown";

const getErrorMessage = (err: any) => {
  // Your apiClient throws Error with fields:
  // err.status, err.details (json or text), err.message = "409 Conflict"
  if (!err) return "Unknown error.";

  // Prefer backend JSON: { message: "..." }
  const details = err.details;
  if (details) {
    if (typeof details === "string" && details.trim()) return details;
    if (typeof details === "object") {
      if (details.message) return String(details.message);
      // sometimes APIs return { title, detail } etc.
      if (details.detail) return String(details.detail);
      if (details.title) return String(details.title);
    }
  }

  // Fall back to generic
  if (err.message) return err.message;

  return "Request failed.";
};

const normalizeStatus = (s: any) => (s || "").toString().trim().toLowerCase();

const InviteAccept = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { token, getToken } = useAuthToken();

  // You may or may not have setters in your UserContext.
  // We’ll update localStorage always, and if setters exist we’ll call them too.
  const userCtx = useUser();
  const setActiveTenantId =
    userCtx?.setActiveTenantId ||
    (userCtx as any)?.setTenant ||
    (userCtx as any)?.setActiveTenant ||
    null;

  const tokenFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const baseUrl = protectedResources.baseURL.endpoint;

  const api = useMemo(() => {
    return createApiClient({
      baseUrl,
      token: token || null,
      getToken: isAuthenticated && token ? getToken : undefined,
      tenantId: null, // base/default
    });
  }, [baseUrl, token, getToken, isAuthenticated]);

  // -----------------------
  // Validate state
  // -----------------------
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [invite, setInvite] = useState<any>(null); // TenantInviteValidateResponseModel
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const [acceptResult, setAcceptResult] = useState<any>(null);

  // -----------------------
  // Validate token (anonymous)
  // -----------------------
  const loadValidate = useCallback(async () => {
    setValidateError("");
    setInvite(null);

    const raw = (tokenFromUrl || "").trim();
    if (!raw) {
      setValidateError("Missing invite token.");
      return;
    }

    setValidateLoading(true);
    try {
      // IMPORTANT: tenantRequired:false
      const res = await api.get("invite/validate", {
        query: { token: raw },
        tenantRequired: false,
      });
      setInvite(res || null);
    } catch (err) {
      setValidateError(getErrorMessage(err));
    } finally {
      setValidateLoading(false);
    }
  }, [api, tokenFromUrl]);

  useEffect(() => {
    // validate does NOT require auth
    if (!api) {
      // If not authenticated, token might be null and api still ok; api is created anyway.
      // But we guard just in case.
      return;
    }
    loadValidate();
  }, [api, loadValidate]);

  // -----------------------
  // Sign-in redirect (preserve token by using current URL)
  // -----------------------
  const startSignIn = async () => {
    const inviteReturnUrl = window.location.href; // keeps token in querystring
    await instance.loginRedirect({
      ...loginRequest,
      prompt: "login",
      redirectStartPage: inviteReturnUrl,
    });
  };

const signOutAndReturn = async () => {
  const inviteReturnUrl = window.location.href;
  await instance.logoutRedirect({
    postLogoutRedirectUri: inviteReturnUrl,
  });
};


  // -----------------------
  // After auth: bootstrap + accept + tenant switch + scoped login
  // -----------------------
const acceptInvite = useCallback(async () => {
  if (!api) return;
  if (!isAuthenticated) return;
  if (!tokenFromUrl?.trim()) return;

  const statusLower = normalizeStatus(invite?.status);
  if (statusLower && statusLower !== "pending") return;

  setAccepting(true);
  setAcceptError("");
  setAcceptSuccess(false);
  setAcceptResult(null);

  try {
    // Step 1: bootstrap login (no tenant header)
    await api.post("userinfo/login", {}, { tenantRequired: false });

    // Step 2: accept invite (tenant header required for EF tenant-write rules)
    const accepted = await api.post(
      "invite/accept",
      { token: tokenFromUrl.trim() },
      { tenantRequired: true, tenantId: invite?.tenantId }
    );

    const newTenantId =
      (accepted as any)?.tenantId || (accepted as any)?.TenantId || (accepted as any)?.tenantID;

    if (!newTenantId) throw new Error("Invite accepted but tenantId was not returned.");

    localStorage.setItem("activeTenantId", newTenantId);
    if (typeof setActiveTenantId === "function") {
      try { setActiveTenantId(newTenantId); } catch {}
    }

    // Step 4: tenant-scoped login
    await api.post("userinfo/login", {}, { tenantRequired: true, tenantId: newTenantId });

    setAcceptSuccess(true);
    setAcceptResult(accepted);

    navigate("/timeentry", { replace: true });
  } catch (err) {
    setAcceptError(getErrorMessage(err));
  } finally {
    setAccepting(false);
  }
}, [
  api,
  isAuthenticated,
  tokenFromUrl,
  invite?.status,
  invite?.tenantId,   // ✅ important
  setActiveTenantId,
  navigate,
]);


  // Auto-accept once authenticated if pending
  useEffect(() => {
    const statusLower = normalizeStatus(invite?.status);

    // If user logs in and invite is pending, accept automatically.
    if (
      isAuthenticated &&
      token &&
      invite &&
      statusLower === "pending" &&
      !accepting &&
      !acceptSuccess &&
      !acceptError
    ) {
      acceptInvite();
    }
  }, [
    isAuthenticated,
    token,
    invite,
    accepting,
    acceptSuccess,
    acceptError,
    acceptInvite,
  ]);

  // -----------------------
  // UI helpers
  // -----------------------
  const statusLower = normalizeStatus(invite?.status);
  const canAccept = statusLower === "pending";
  const wrongAccount =
    isAuthenticated &&
    canAccept &&
    acceptError &&
    acceptError.toLowerCase().includes("does not match invite email");

  const badgeClass = (status: any) => {
    const s = normalizeStatus(status);
    if (s === "pending") return styles.badgePending;
    if (s === "accepted") return styles.badgeAccepted;
    if (s === "revoked") return styles.badgeRevoked;
    if (s === "expired") return styles.badgeExpired;
    return styles.badgeDefault;
  };

  return (
    <div className={`main-articles ${styles.container}`}>
      <div className={styles.headerSection}>
        <h2 className={styles.heading}>Accept Invite</h2>
      </div>

      {/* Status / errors */}
      {validateError && (
        <div className={styles.errorBubble}>
          <div className={styles.errorTitle}>Error</div>
          <div className={styles.errorMessage}>{validateError}</div>
        </div>
      )}

      {acceptError && (
        <div className={styles.errorBubble}>
          <div className={styles.errorTitle}>
            Could not accept invite
          </div>
          <div className={styles.errorMessage}>{acceptError}</div>
        </div>
      )}

      {validateLoading ? (
        <div className={styles.bubble}>Loading invite…</div>
      ) : !invite ? (
        <div className={styles.bubble}>
          <div className={styles.muted}>
            {tokenFromUrl
              ? "Invite not found or not available."
              : "No token provided."}
          </div>
        </div>
      ) : (
        <div className={styles.bubble}>
          <div className={styles.inviteHeader}>
            <div className={styles.inviteDetails}>
              <div className={styles.label}>
                Tenant
              </div>
              <div className={styles.tenantName}>
                {invite.tenantName || "—"}
              </div>

              <div className={styles.metaRow}>
                <span className={`${styles.badge} ${badgeClass(invite.status)}`}>
                  {invite.status || "—"}
                </span>

                <span className={styles.metaText}>
                  Role:{" "}
                  <span className={styles.metaValue}>
                    {roleLabel(invite.roleId)}
                  </span>
                </span>

                <span className={styles.metaText}>
                  Expires:{" "}
                  <span className={styles.metaValue}>
                    {fmtDateTime(invite.expiresAt)}
                  </span>
                </span>
              </div>

              <div className={styles.invitedEmail}>
                Invited email:{" "}
                <span className={styles.bold}>{invite.email || "—"}</span>
              </div>
            </div>
          </div>

          <div className={styles.actionSection}>
            {/* If not pending */}
            {!canAccept && (
              <div className={styles.muted}>
                This invite cannot be accepted because it is{" "}
                <span className={styles.metaValue}>
                  {invite.status}
                </span>
                .
              </div>
            )}

            {/* Pending: not logged in */}
            {canAccept && !isAuthenticated && (
              <>
                <div className={styles.signInPrompt}>
                  To accept this invite, sign in (or create an account).
                </div>
                <button
                  type="button"
                  onClick={startSignIn}
                  className={styles.btnPrimary}
                >
                  Sign in / Create account
                </button>
              </>
            )}

            {/* Pending: logged in, token not ready */}
            {canAccept && isAuthenticated && !token && (
              <div className={styles.actionRow}>
                <div className={styles.muted}>Finishing sign-in…</div>
                <button
                  type="button"
                  onClick={signOutAndReturn}
                  className={styles.btnSecondary}
                >
                  Sign out
                </button>
              </div>
            )}

            {/* Pending: logged in, token ready (auto-accept runs), but provide manual button too */}
            {canAccept && isAuthenticated && token && (
              <>
                {wrongAccount && (
                  <div className={styles.wrongAccountWarning}>
                    You're signed in with the wrong account. Please sign out and
                    sign in with:{" "}
                    <span className={styles.darkText}>{invite?.email}</span>
                  </div>
                )}

                <div className={styles.actionRow}>
                  <button
                    type="button"
                    onClick={acceptInvite}
                    disabled={accepting || acceptSuccess}
                    className={styles.btnPrimary}
                  >
                    {accepting
                      ? "Accepting…"
                      : acceptSuccess
                        ? "Accepted"
                        : "Accept invite"}
                  </button>

                  <button
                    type="button"
                    onClick={loadValidate}
                    disabled={accepting}
                    className={styles.btnSecondary}
                  >
                    Refresh status
                  </button>

                  <button
                    type="button"
                    onClick={signOutAndReturn}
                    className={styles.btnSecondary}
                  >
                    Sign out
                  </button>

                  {acceptResult?.alreadyMember && (
                    <div className={styles.alreadyMemberNote}>
                      You were already a member of this tenant. We marked the
                      invite accepted.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteAccept;
